/**
 * Layer 1: LayoutTheme Context
 *
 * Provides the current layout theme and allows switching between different
 * component structures and interaction patterns.
 *
 * PI5: Integrated with theme transition utility for smooth theme switching
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react'
import type { LayoutTheme, LayoutThemeConfig } from '@/types/layout-theme'
import { LAYOUT_THEME_CONFIGS } from '@/types/layout-theme'
import { withThemeTransition } from '@/lib/theme-transition'

interface LayoutThemeContextValue {
  currentTheme: LayoutTheme
  themeConfig: LayoutThemeConfig
  setTheme: (theme: LayoutTheme) => void
  availableThemes: LayoutTheme[]
}

const LayoutThemeContext = createContext<LayoutThemeContextValue | undefined>(undefined)

interface LayoutThemeProviderProps {
  children: ReactNode
  initialTheme?: LayoutTheme
}

export function LayoutThemeProvider({
  children,
  initialTheme,
}: LayoutThemeProviderProps) {
  // PI3: Read initial theme from URL params (for preview), HTML attribute, or localStorage
  const getInitialTheme = (): LayoutTheme => {
    if (initialTheme) return initialTheme
    if (typeof window !== 'undefined') {
      // Check URL parameters first (for iframe previews)
      const urlParams = new URLSearchParams(window.location.search)
      const urlTheme = urlParams.get('theme')
      if (urlTheme && urlTheme in LAYOUT_THEME_CONFIGS) {
        return urlTheme as LayoutTheme
      }

      // Fall back to HTML attribute (set by blocking script)
      const htmlTheme = document.documentElement.getAttribute('data-layout-theme')
      if (htmlTheme && htmlTheme in LAYOUT_THEME_CONFIGS) {
        return htmlTheme as LayoutTheme
      }
    }
    return 'modern-cards'
  }

  const [currentTheme, setCurrentTheme] = useState<LayoutTheme>(getInitialTheme())

  // PI8: Sync theme changes across browser tabs (but not in preview mode)
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Don't sync if we're in preview mode
    const urlParams = new URLSearchParams(window.location.search)
    const isPreview = urlParams.get('preview') === 'true'
    if (isPreview) return

    const handleStorageChange = (event: StorageEvent) => {
      // Only react to layoutTheme changes from other tabs
      if (event.key === 'layoutTheme' && event.newValue) {
        const newTheme = event.newValue as LayoutTheme
        if (newTheme in LAYOUT_THEME_CONFIGS && newTheme !== currentTheme) {
          setCurrentTheme(newTheme)
          document.documentElement.setAttribute('data-layout-theme', newTheme)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [currentTheme])

  const setTheme = useCallback((theme: LayoutTheme) => {
    // PI5: Wrap theme change with smooth transition
    withThemeTransition(() => {
      setCurrentTheme(theme)

      // Update HTML data attribute for CSS selectors
      if (typeof window !== 'undefined') {
        document.documentElement.setAttribute('data-layout-theme', theme)

        // Don't persist to localStorage if we're in preview mode
        const urlParams = new URLSearchParams(window.location.search)
        const isPreview = urlParams.get('preview') === 'true'
        if (!isPreview) {
          localStorage.setItem('layoutTheme', theme)
        }
      }
    })
  }, [])

  const value: LayoutThemeContextValue = {
    currentTheme,
    themeConfig: LAYOUT_THEME_CONFIGS[currentTheme],
    setTheme,
    availableThemes: Object.keys(LAYOUT_THEME_CONFIGS) as LayoutTheme[],
  }

  return (
    <LayoutThemeContext.Provider value={value}>
      {children}
    </LayoutThemeContext.Provider>
  )
}

export function useLayoutTheme() {
  const context = useContext(LayoutThemeContext)
  if (!context) {
    throw new Error('useLayoutTheme must be used within LayoutThemeProvider')
  }
  return context
}
